"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { Lock, Globe, Key, TrendingUp } from "lucide-react";
import { getImageUrl } from "../../utils/imageUtils";

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  hook: string;
  delay: number;
  isVisible: boolean;
}

// Bitcoin Logo URL - zentrale Funktion für korrekte Umgebungserkennung
const getBitcoinLogoUrl = (): string => getImageUrl("/logos/crypto/btc.svg");

function FeatureCard({
  icon,
  title,
  description,
  hook,
  delay,
  isVisible,
}: FeatureCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(30px)",
        transition: `opacity 0.8s ease-out ${delay}s, transform 0.8s ease-out ${delay}s`,
      }}
    >
      <div
        className="landing-card-glow bitcoin-feature-card"
        style={{
          overflow: "hidden",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={{ padding: "1.5rem" }}>

          {/* Icon with 3D Coin-Flip Effect */}
          <div
            style={{
              perspective: "600px",
              width: "clamp(48px, 10vw, 64px)",
              height: "clamp(48px, 10vw, 64px)",
              marginBottom: "16px",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <div
              style={{
                position: "relative",
                width: "100%",
                height: "100%",
                transformStyle: "preserve-3d",
                transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: isHovered ? "rotateY(180deg)" : "rotateY(0deg)",
              }}
            >
              {/* Front Side - Feature Icon */}
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backfaceVisibility: "hidden",
                  backgroundColor: "transparent",
                  border: "1px solid rgba(247, 147, 26, 0.35)",
                  boxShadow: "0 0 12px rgba(247, 147, 26, 0.08)",
                  zIndex: 10,
                }}
              >
                <div style={{ color: "#f7931a" }}>{icon}</div>
              </div>

              {/* Back Side - Bitcoin Logo */}
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backfaceVisibility: "hidden",
                  backgroundColor: "transparent",
                  transform: "rotateY(180deg)",
                  zIndex: 10,
                  overflow: "hidden",
                }}
              >
                <img
                  src={getBitcoinLogoUrl()}
                  alt="Bitcoin"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Title */}
          <h3
            style={{
              fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
              fontWeight: 700,
              fontSize: "clamp(1.05rem, 2.5vw, 1.35rem)",
              color: "#f7931a",
              marginBottom: "8px",
              position: "relative",
              zIndex: 10,
              textAlign: "center",
            }}
          >
            {title}
          </h3>

          {/* Description */}
          <p
            style={{
              fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
              fontSize: "clamp(0.85rem, 1.5vw, 1rem)",
              lineHeight: "160%",
              color: "#b7ab98",
              position: "relative",
              zIndex: 10,
              textAlign: "center",
            }}
          >
            {description}
          </p>

          {/* Expandable Hook Section */}
          <div
            style={{
              maxHeight: isHovered ? "200px" : "0px",
              opacity: isHovered ? 1 : 0,
              overflow: "hidden",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              marginTop: isHovered ? "16px" : "0px",
              paddingTop: isHovered ? "16px" : "0px",
              borderTop: isHovered ? "1px solid hsla(37, 18%, 66%, 0.15)" : "1px solid transparent",
            }}
          >
            <p
              style={{
                fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
                fontSize: "0.875rem",
                lineHeight: "170%",
                color: "hsla(37, 18%, 66%, 0.7)",
                position: "relative",
                zIndex: 10,
                textAlign: "center",
                fontStyle: "italic",
              }}
            >
              {hook}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const features = [
  {
    icon: <Lock size={28} strokeWidth={1.5} />,
    title: "Begrenzt",
    description: "21 Millionen. Der Konsens aller Nutzer.",
    hook: "Die Obergrenze ist keine technische Sperre, sondern ein Versprechen der Gemeinschaft an sich selbst. Wer würde für seine eigene Entwertung stimmen?",
  },
  {
    icon: <Globe size={28} strokeWidth={1.5} />,
    title: "Dezentral",
    description: "Verteilt auf der ganzen Welt. Unaufhaltbar.",
    hook: "Kein Server zum Abschalten, keine Firma zum Verklagen, kein Land zum Sanktionieren. Bitcoin ist überall - und nirgends.",
  },
  {
    icon: <Key size={28} strokeWidth={1.5} />,
    title: "Souverän",
    description: "Dein Geld. Deine Regeln. Jederzeit.",
    hook: "Kein Antrag, keine Wartezeit, keine Genehmigung. Bitcoin fragt nicht um Erlaubnis - denn finanzielle Freiheit braucht keinen Stempel.",
  },
  {
    icon: <TrendingUp size={28} strokeWidth={1.5} />,
    title: "Bewährt",
    description: "15 Jahre. Kein Ausfall. Kein Reset.",
    hook: "Jeder Hack-Versuch, jedes Verbot, jede Krise - Bitcoin läuft weiter. Unverändert. Ungebrochen.",
  },
];

export function BitcoinSolutionSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [showContent, setShowContent] = useState(false);
  const rafRef = useRef<number | undefined>(undefined);

  // Mouse tracking for glow effect (like /ui page)
  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      const cards = document.querySelectorAll(".bitcoin-feature-card");

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        (card as HTMLElement).style.setProperty("--xPos", `${x}px`);
        (card as HTMLElement).style.setProperty("--yPos", `${y}px`);
      });
    };

    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const checkVisible = useCallback(() => {
    if (!sectionRef.current || showContent) return;

    const rect = sectionRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    if (rect.top < windowHeight * 0.7) {
      setShowContent(true);
    }
  }, [showContent]);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(checkVisible);
    };

    checkVisible();

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [checkVisible]);

  return (
    <section
      ref={sectionRef}
      style={{
        position: "relative",
        width: "100%",
        overflow: "hidden",
        minHeight: "100vh",
        backgroundColor: "#0d0d0d",
      }}
    >
      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-48">

        {/* Main Headline */}
        <h2
          style={{
            fontFamily: '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif',
            fontWeight: 700,
            fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
            letterSpacing: "-0.03em",
            lineHeight: "110%",
            color: "#b7ab98",
            textAlign: "center",
            userSelect: "none",
            marginBottom: "80px",
            opacity: showContent ? 1 : 0,
            transform: showContent ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 1s ease-out 0.2s, transform 1s ease-out 0.2s",
          }}
        >
          <span style={{ color: "#f7931a" }}>BITCOIN</span>
          <div style={{ height: "0.3em" }} />
          Geld, das dir gehört.
        </h2>

        {/* Feature Cards Grid with Glow Effect */}
        <div
          className="glow-grid bitcoin-features-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "24px",
            alignItems: "start",
          }}
        >
          {features.map((feature, idx) => (
            <FeatureCard
              key={feature.title}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              hook={feature.hook}
              delay={0.5 + idx * 0.15}
              isVisible={showContent}
            />
          ))}
        </div>
      </div>

    </section>
  );
}
